import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Edit, User, LogOut, ArrowUpCircle, ArrowDownCircle, Building, MapPin, X } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { useAuthContext } from '../contexts/AuthContext';
import { gradientButton } from '../theme';
import ImageCropper from '../components/ImageCropper';
import { useCachedData } from '../hooks/useCachedData';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

// Définir une interface pour le type DatingProfile
export interface DatingProfile {
  country: string;
  city: string;
  gender: string;
  age: string;
  relationType: string;
  shortTermIntentions: {
    money: string;
    moneyFlow: string;
    hookup: boolean;
    resto: boolean;
    drink: boolean;
    seeWhat: boolean;
    other: boolean;
  };
  longTermIntentions: {
    marriage: boolean;
    children: boolean;
    fidelity: boolean;
    polygamy: boolean;
    free: boolean;
  };
  secretCode: string;
  isVisible: boolean;
  photos: string[];
  sexualOrientation: string;
}

// Définir un type pour les valeurs du formulaire
interface FormValues {
  country: string;
  city: string;
  gender: string;
  age: string;
  moneyAmount: string;
  moneyFlow: string;
  sexualOrientation: string;
}

function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateProfile, uploadProfilePhoto, deleteProfilePhoto, resetContactClicks, refresh } = useProfile();
  const { signOut, user } = useAuthContext();

  // État pour détecter si c'est un nouvel utilisateur
  const [isNewUser, setIsNewUser] = useState(false);

  // Ajout d'un état pour suivre l'enregistrement en cours
  const [isSaving, setIsSaving] = useState(false);
  // Ajout d'un état pour suivre si le formulaire a été modifié
  const [formIsDirty, setFormIsDirty] = useState(false);

  // Vérifier si c'est un nouvel utilisateur lorsque le composant est monté
  useEffect(() => {
    const checkIsNewUser = async () => {
      // D'abord vérifier dans la base de données
      if (user?.id) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('is_new_user')
            .eq('id', user.id)
            .single();
          
          if (!error && data) {
            setIsNewUser(data.is_new_user || false);
            console.log("[Settings] is_new_user from database:", data.is_new_user);
            return;
          }
        } catch (err) {
          console.error("[Settings] Error checking is_new_user in database:", err);
        }
      }
      
      // Fallback à localStorage
    const isNewUserFlag = localStorage.getItem('isNewUser') === 'true';
    setIsNewUser(isNewUserFlag);
      console.log("[Settings] isNewUser from localStorage:", isNewUserFlag);
    };
    
    checkIsNewUser();
  }, [user?.id]);

  // Utiliser useCachedData pour le profil avec une durée de cache de 30 minutes
  const { data: profile, invalidateCache } = useCachedData(
    `settings-profile-${user?.id}`,
    async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    30 * 60 * 1000, // 30 minutes
    [user?.id]
  );

  const [userProfile, setUserProfile] = useState({
    name: '',
    photo: '',
    customLink: '',
    customLinkUrl: '',
    email: '',
    description: ''
  });

  // Référence pour le champ de lien de contact
  const contactLinkRef = useRef<HTMLInputElement>(null);

  // État indépendant pour la visibilité du profil
  const [isProfileVisible, setIsProfileVisible] = useState(false);
  // État indépendant pour les photos
  const [profilePhotos, setProfilePhotos] = useState<string[]>([]);
  // État pour le type de relation sélectionné (pour l'UI uniquement)
  const [selectedRelationType, setSelectedRelationType] = useState('');
  
  // État pour suivre les intentions long terme
  const [longTermIntentions, setLongTermIntentions] = useState({
      marriage: false,
      children: false,
      fidelity: false,
      polygamy: false,
      free: false
  });
  
  // NOUVELLE APPROCHE: État unifié pour les valeurs de formulaire
  const [formValues, setFormValues] = useState<FormValues>({
    country: '',
    city: '',
    gender: '',
    age: '',
    moneyAmount: '',
    moneyFlow: 'give',
    sexualOrientation: 'hetero'
  });
  
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [tempPhotoUrl, setTempPhotoUrl] = useState<string | null>(null);

  // Toutes les références pour les champs non contrôlés
  const countryInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const ageInputRef = useRef<HTMLInputElement>(null);
  const moneyAmountInputRef = useRef<HTMLInputElement>(null);
  const genderSelectRef = useRef<HTMLSelectElement>(null);
  const hookupRef = useRef<HTMLInputElement>(null);
  const marriageRef = useRef<HTMLInputElement>(null);
  const childrenRef = useRef<HTMLInputElement>(null);
  const fidelityRef = useRef<HTMLInputElement>(null);
  const polygamyRef = useRef<HTMLInputElement>(null);
  const freeRef = useRef<HTMLInputElement>(null);
  const restoRef = useRef<HTMLInputElement>(null);
  const drinkRef = useRef<HTMLInputElement>(null);
  const seeWhatRef = useRef<HTMLInputElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);

  // Référence pour suivre si une invalidation de cache est en cours
  const isCacheInvalidationInProgressRef = useRef(false);
  // Référence pour stocker les valeurs du formulaire pendant l'invalidation du cache
  const formValuesBeforeCacheInvalidationRef = useRef<FormValues | null>(null);
  // Référence pour stocker l'état des cases à cocher des intentions long terme
  const longTermIntentionsStateRef = useRef<{
    marriage: boolean;
    children: boolean;
    fidelity: boolean;
    polygamy: boolean;
    free: boolean;
  } | null>(null);

  // Référence pour stocker l'état complet du formulaire
  const completeFormStateRef = useRef<{
    userProfile: {
      name: string;
      photo: string;
      customLink: string;
      customLinkUrl: string;
      email: string;
      description: string;
    };
    formValues: FormValues;
    longTermIntentions: {
      marriage: boolean;
      children: boolean;
      fidelity: boolean;
      polygamy: boolean;
      free: boolean;
    };
    selectedRelationType: string;
    isProfileVisible: boolean;
    profilePhotos: string[];
    fieldValues: {
      country: string;
      city: string;
      gender: string;
      age: string;
      moneyAmount: string;
      moneyFlow: string;
      shortTermIntentions: {
        hookup: boolean;
        resto: boolean;
        drink: boolean;
        seeWhat: boolean;
        other: boolean;
      };
    };
  } | null>(null);

  // Nouvelle fonction pour capturer l'état complet du formulaire
  const captureFormState = useCallback(() => {
    // Récupérer les valeurs des champs non contrôlés
    const fieldValues = {
      country: countryInputRef.current?.value || '',
      city: cityInputRef.current?.value || '',
      gender: genderSelectRef.current?.value || '',
      age: ageInputRef.current?.value || '',
      moneyAmount: moneyAmountInputRef.current?.value || '',
      moneyFlow: formValues.moneyFlow,
      shortTermIntentions: {
        hookup: hookupRef.current?.checked || false,
        resto: restoRef.current?.checked || false,
        drink: drinkRef.current?.checked || false,
        seeWhat: seeWhatRef.current?.checked || false,
        other: otherRef.current?.checked || false
      }
    };

    // Sauvegarder l'état complet
    completeFormStateRef.current = {
      userProfile,
      formValues,
      longTermIntentions,
      selectedRelationType,
      isProfileVisible,
      profilePhotos,
      fieldValues
    };

    console.log('État du formulaire capturé:', completeFormStateRef.current);
  }, [userProfile, formValues, longTermIntentions, selectedRelationType, isProfileVisible, profilePhotos]);

  // Nouvelle fonction pour restaurer l'état complet du formulaire
  const restoreFormState = useCallback(() => {
    if (!completeFormStateRef.current) return;

    console.log('Restauration de l\'état du formulaire:', completeFormStateRef.current);
    
    // Restaurer les états React
    setUserProfile({
      ...completeFormStateRef.current.userProfile,
      description: userProfile.description // Utiliser la valeur actuelle
    });
    setFormValues(completeFormStateRef.current.formValues);
    setLongTermIntentions(completeFormStateRef.current.longTermIntentions);
    setSelectedRelationType(completeFormStateRef.current.selectedRelationType);
    setIsProfileVisible(completeFormStateRef.current.isProfileVisible);
    setProfilePhotos(completeFormStateRef.current.profilePhotos);
    
    // Restaurer les valeurs des champs non contrôlés
    if (countryInputRef.current) {
      countryInputRef.current.value = completeFormStateRef.current.fieldValues.country;
    }
    if (cityInputRef.current) {
      cityInputRef.current.value = completeFormStateRef.current.fieldValues.city;
    }
    if (genderSelectRef.current) {
      genderSelectRef.current.value = completeFormStateRef.current.fieldValues.gender;
    }
    if (ageInputRef.current) {
      ageInputRef.current.value = completeFormStateRef.current.fieldValues.age;
    }
    if (moneyAmountInputRef.current && completeFormStateRef.current.selectedRelationType !== 'long') {
      moneyAmountInputRef.current.value = completeFormStateRef.current.fieldValues.moneyAmount;
    }
    
    // Restaurer les checkboxes pour les intentions court terme
    if (hookupRef.current) {
      hookupRef.current.checked = completeFormStateRef.current.fieldValues.shortTermIntentions.hookup;
    }
    if (restoRef.current) {
      restoRef.current.checked = completeFormStateRef.current.fieldValues.shortTermIntentions.resto;
    }
    if (drinkRef.current) {
      drinkRef.current.checked = completeFormStateRef.current.fieldValues.shortTermIntentions.drink;
    }
    if (seeWhatRef.current) {
      seeWhatRef.current.checked = completeFormStateRef.current.fieldValues.shortTermIntentions.seeWhat;
    }
    if (otherRef.current) {
      otherRef.current.checked = completeFormStateRef.current.fieldValues.shortTermIntentions.other;
    }
  }, []);

  // Marquer le formulaire comme modifié
  const markAsDirty = () => {
    if (!formIsDirty) {
      setFormIsDirty(true);
      // Capturer l'état initial quand le formulaire devient "dirty"
      captureFormState();
    }
  };

  // Gestionnaire pour changer le type de relation (seulement pour l'UI)
  const handleRelationTypeChange = (newType: string) => {
    // Sauvegarder les valeurs des champs avant le changement
    markAsDirty();
    
    console.log('Changement de type de relation vers:', newType);
    setSelectedRelationType(newType);
    
    // Si on passe en mode "relation longue", on efface le montant d'argent
    if (newType === 'long') {
      // Mise à jour de formValues pour cohérence (même si le champ est non contrôlé)
      setFormValues(prev => ({
        ...prev,
        moneyAmount: ''
      }));
      
      // Vider explicitement la valeur du champ input via la référence
      if (moneyAmountInputRef.current) {
        moneyAmountInputRef.current.value = '';
      }
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    if (selectedPhotoIndex !== null) {
      // Cas des photos supplémentaires
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      
      // Capturer l'état du formulaire avant l'opération au cas où
      if (formIsDirty) {
        captureFormState();
      }
      
      const { publicUrl, error } = await uploadProfilePhoto(file, selectedPhotoIndex);
      
      if (!error && publicUrl) {
        // Mise à jour indépendante des photos dans le state local
        setProfilePhotos(prev => [
          ...prev.slice(0, selectedPhotoIndex),
            publicUrl,
          ...prev.slice(selectedPhotoIndex + 1)
        ]);
        
        // Nous n'avons plus besoin d'invalider le cache complet ici
        // car useProfile.ts utilise maintenant safeInvalidatePhotoCache
        // await safeInvalidateCache();
      }
    } else {
      // Cas de la photo de profil
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      
      // Capturer l'état du formulaire avant l'opération au cas où
      if (formIsDirty) {
        captureFormState();
      }
      
      const { publicUrl, error } = await uploadProfilePhoto(file, null);
      
      if (!error && publicUrl) {
        // Mise à jour de la photo de profil dans le state local
        setUserProfile(prev => ({ ...prev, photo: publicUrl }));
        
        // Nous n'avons plus besoin d'invalider le cache complet ici
        // car useProfile.ts utilise maintenant safeInvalidatePhotoCache
        // await safeInvalidateCache();
      }
    }
    
    setShowImageCropper(false);
    setTempPhotoUrl(null);
    setSelectedPhotoIndex(null);
  };

  const handlePhotoFileSelect = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    
    if (file) {
      // Sauvegarder les valeurs des champs avant l'upload
      markAsDirty();
      
      // Vérification du type et de la taille du fichier
      if (!file.type.startsWith('image/')) {
        alert(t('settings.photoFormatError'));
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        alert(t('settings.photoSizeError'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setTempPhotoUrl(e.target.result as string);
          setSelectedPhotoIndex(index);
          setShowImageCropper(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction modifiée pour la suppression d'une photo
  const handleDeletePhoto = async (index: number) => {
    // Capturer l'état du formulaire avant l'opération au cas où
    if (formIsDirty) {
      captureFormState();
    }
    
    const { error } = await deleteProfilePhoto(index);
    
    if (!error) {
      // Mise à jour du state local pour refléter la suppression
      setProfilePhotos(prev => prev.filter((_, i) => i !== index));
      
      // Nous n'avons plus besoin d'invalider le cache complet ici
      // car useProfile.ts utilise maintenant safeInvalidatePhotoCache
      // await safeInvalidateCache();
    } else {
      console.error('Erreur lors de la suppression de la photo:', error);
    }
  };

  // Fonction simplifiée pour enregistrer tous les paramètres
  const handleSaveDatingProfile = async () => {
    try {
      // Activer l'animation de chargement
      setIsSaving(true);
      
      // Créer un objet pour les mises à jour du profil
      const profileUpdates: any = {
        relation_type: selectedRelationType,
        is_new_user: false // Marquer explicitement que ce n'est plus un nouvel utilisateur
      };
      
      // Ajouter le nom d'utilisateur aux mises à jour
      profileUpdates.full_name = userProfile.name;
      
      // Ajouter le lien personnalisé s'il existe
      profileUpdates.custom_link_url = userProfile.customLinkUrl;
      
      // Ajouter la description
      profileUpdates.description = userProfile.description;
      
      // Récupérer et ajouter les valeurs pour pays, ville, sexe et âge
      if (countryInputRef.current) {
        profileUpdates.country = countryInputRef.current.value;
      }

      if (cityInputRef.current) {
        profileUpdates.city = cityInputRef.current.value;
      }

      if (genderSelectRef.current) {
        profileUpdates.gender = genderSelectRef.current.value;
      }

      if (ageInputRef.current) {
        profileUpdates.age = ageInputRef.current.value;
      }

      // Ajouter l'orientation sexuelle
      profileUpdates.sexual_orientation = formValues.sexualOrientation;

      // Ajouter les valeurs pour les intentions à court terme si pertinent
      if (selectedRelationType === 'court') {
        profileUpdates.money_amount = moneyAmountInputRef.current?.value || '';
        profileUpdates.money_flow = formValues.moneyFlow;
        
        // Récupérer les intentions à court terme
        const shortTermIntentions = [];
        if (hookupRef.current?.checked) shortTermIntentions.push('hookup');
        if (restoRef.current?.checked) shortTermIntentions.push('resto');
        if (drinkRef.current?.checked) shortTermIntentions.push('drink');
        if (seeWhatRef.current?.checked) shortTermIntentions.push('seeWhat');
        if (otherRef.current?.checked) shortTermIntentions.push('other');
        
        profileUpdates.short_term_intentions = shortTermIntentions;
      }

      // Ajouter les valeurs pour les intentions à long terme si pertinent
      if (selectedRelationType === 'long') {
        const longTermIntentionsArray = [];
        if (longTermIntentions.marriage) longTermIntentionsArray.push('marriage');
        if (longTermIntentions.children) longTermIntentionsArray.push('children');
        if (longTermIntentions.fidelity) longTermIntentionsArray.push('fidelity');
        if (longTermIntentions.polygamy) longTermIntentionsArray.push('polygamy');
        if (longTermIntentions.free) longTermIntentionsArray.push('free');
        
        profileUpdates.long_term_intentions = longTermIntentionsArray;
      }

      // Mise à jour de la visibilité
      profileUpdates.is_visible = isProfileVisible;
      
      // Mise à jour du profil
      console.log('Mise à jour du profil avec:', JSON.stringify(profileUpdates, null, 2));
      await updateProfile(profileUpdates);
      
      // Réinitialiser l'indicateur de succès
      setSaveSuccess(true);
      
      // Invalider le cache pour s'assurer que les changements sont visibles immédiatement
      await invalidateCache();
      
      // Afficher le toast de succès
      toast.success(t('profile.saveSuccess'));
      
      // Si c'est un nouvel utilisateur, supprimer le flag isNewUser
      if (isNewUser) {
        localStorage.removeItem('isNewUser');
        setIsNewUser(false);
        console.log("[Settings] Removed new user flag after saving profile");
      }
      
      // Attendre un court délai avant de rediriger
      setTimeout(() => {
        // Rediriger vers la page profil
        navigate('/profile');
      }, 1000);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil:', error);
      toast.error(t('profile.saveError'));
    } finally {
      // Désactiver l'animation de chargement après 1 seconde
      setTimeout(() => {
        setIsSaving(false);
        setSaveSuccess(false);
      }, 1000);
    }
  };

  // Fonction modifiée pour se déconnecter
  const handleLogout = async () => {
    await signOut();
    // Utiliser window.location.replace pour une redirection forcée immédiate
    window.location.replace(`${window.location.origin}/#/login`);
  };

  // Fonction simplifiée pour naviguer avec vérification
  const navigateWithCheck = (path: string) => {
    navigate(path);
  };

  const ProfilePhotoSection = () => {
    const handlePhotoUploadClick = () => {
      document.getElementById('photo-upload')?.click();
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      
      if (file) {
        // Marquer le formulaire comme modifié
        markAsDirty();
        
        // Vérification du type et de la taille du fichier
        if (!file.type.startsWith('image/')) {
          alert(t('settings.photoFormatError'));
          return;
        }
        
        if (file.size > 5 * 1024 * 1024) { // 5MB
          alert(t('settings.photoSizeError'));
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setTempPhotoUrl(e.target.result as string);
            setShowImageCropper(true);
          }
        };
        reader.readAsDataURL(file);
      }
    };

    return (
      <div className="flex justify-center">
        <div 
          className="relative w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center cursor-pointer"
          onClick={handlePhotoUploadClick}
        >
          {userProfile?.photo ? (
            <img
              src={userProfile.photo}
              alt={t('settings.profilePhotoAlt')} 
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-12 h-12 text-gray-400" />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <Edit className="w-6 h-6 text-white" />
          </div>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>
    );
  };

  const DatingProfileSection = () => {
    const handleAddPhoto = (index: number) => {
      document.getElementById(`photo-upload-${index}`)?.click();
    };

    return (
      <div className="mt-6 pt-4 border-t border-gray-800">
        <h3 className="text-lg font-medium text-white mb-3 bg-gradient-to-r from-primary-start to-primary-end bg-clip-text text-transparent">
          {t('settings.datingProfile.title')}
        </h3>

        <div className="space-y-3">
          {/* Section Photos */}
          <div className="bg-[#0A0A0A] rounded-xl p-3">
            <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.photos')}</div>
            <div className="grid grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="aspect-square relative">
                  {profilePhotos?.[index] ? (
                    <div className="w-full h-full relative group">
                      <img
                        src={profilePhotos[index]}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          onClick={() => handleDeletePhoto(index)}
                          className="text-white bg-red-500/80 p-2 rounded-full hover:bg-red-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(index)}
                        className="absolute top-2 right-2 bg-white rounded-full p-1"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleAddPhoto(index)}
                        className="w-full h-full border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center hover:border-primary-end transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                      <input 
                        id={`photo-upload-${index}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoFileSelect(e, index)}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Type de relation */}
          <div className="bg-[#0A0A0A] rounded-xl p-3">
            <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.relationType')}</div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  console.log('AVANT changement court terme - Pays:', formValues.country);
                  handleRelationTypeChange('court');
                  console.log('APRÈS changement court terme - Pays:', formValues.country);
                }}
                className={`px-3 py-1.5 rounded-lg text-white transition-colors flex items-center justify-center ${
                  selectedRelationType === 'court' 
                    ? 'border-2 border-primary-end text-primary-end' 
                    : 'border border-gray-700 hover:border-primary-end/50'
                } bg-transparent flex-1 text-sm`}
              >
                <span>{t('profile.datingProfile.relationTypes.short')}</span>
                <div className="flex ml-2 space-x-1">
                  <span className="animate-float" style={{ animationDelay: '0s' }}>🍆</span>
                  <span className="animate-float" style={{ animationDelay: '0.3s' }}>🍑</span>
                  <span className="animate-float" style={{ animationDelay: '0.9s' }}>💵</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  console.log('AVANT changement long terme - Pays:', formValues.country);
                  handleRelationTypeChange('long');
                  console.log('APRÈS changement long terme - Pays:', formValues.country);
                }}
                className={`px-3 py-1.5 rounded-lg text-white transition-colors flex-1 flex items-center justify-center ${
                  selectedRelationType === 'long'
                    ? 'border-2 border-primary-end text-primary-end'
                    : 'border border-gray-700 hover:border-primary-end/50'
                } bg-transparent text-sm`}
              >
                <span>{t('profile.datingProfile.relationTypes.long')}</span>
                <div className="flex ml-2 space-x-1">
                  <span className="animate-float" style={{ animationDelay: '0s' }}>💘</span>
                  <span className="animate-float" style={{ animationDelay: '0.3s' }}>💍</span>
                  <span className="animate-float" style={{ animationDelay: '0.6s' }}>👶</span>
                </div>
              </button>
            </div>
          </div>

          {/* Intentions Court terme */}
          <div className={`bg-[#0A0A0A] rounded-xl p-3 ${selectedRelationType === 'court' ? '' : 'opacity-50'}`}>
            <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.shortTermIntentions')}</div>
            <div className="space-y-2">
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.moneyAmount')}</div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  ref={moneyAmountInputRef}
                  defaultValue={formValues.moneyAmount}
                  onChange={markAsDirty}
                  disabled={selectedRelationType !== 'court'}
                  placeholder={t('profile.datingProfile.moneyPlaceholder')}
                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                />
                
                {/* Sélecteur Donner/Recevoir */}
                <div className="flex mt-2 space-x-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 rounded-lg border transition-colors px-3 py-2 cursor-pointer ${
                    formValues.moneyFlow === 'give' 
                      ? 'border-green-500 bg-black/50 text-green-500'
                      : 'border-gray-700 bg-black text-gray-400 hover:border-gray-600'
                  }`}>
                    <input
                      type="radio"
                      name="moneyFlow"
                      value="give"
                      className="sr-only"
                      checked={formValues.moneyFlow === 'give'}
                      onChange={() => {
                        setFormValues(prev => ({ ...prev, moneyFlow: 'give' }));
                        markAsDirty();
                      }}
                      disabled={selectedRelationType !== 'court'}
                    />
                    <ArrowUpCircle className={`h-5 w-5 ${formValues.moneyFlow === 'give' ? 'animate-float' : ''}`} />
                    <span className="text-sm font-medium">Donner</span>
                  </label>
                  
                  <label className={`flex-1 flex items-center justify-center gap-2 rounded-lg border transition-colors px-3 py-2 cursor-pointer ${
                    formValues.moneyFlow === 'receive' 
                      ? 'border-primary-end bg-black/50 text-primary-end'
                      : 'border-gray-700 bg-black text-gray-400 hover:border-gray-600'
                  }`}>
                    <input
                      type="radio"
                      name="moneyFlow"
                      value="receive"
                      className="sr-only"
                      checked={formValues.moneyFlow === 'receive'}
                      onChange={() => {
                        setFormValues(prev => ({ ...prev, moneyFlow: 'receive' }));
                        markAsDirty();
                      }}
                      disabled={selectedRelationType !== 'court'}
                    />
                    <ArrowDownCircle className={`h-5 w-5 ${formValues.moneyFlow === 'receive' ? 'animate-float' : ''}`} />
                    <span className="text-sm font-medium">Recevoir</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    ref={hookupRef}
                    defaultChecked={profile?.short_term_intentions?.includes('hookup')}
                    onChange={markAsDirty}
                    disabled={selectedRelationType !== 'court'}
                    className="form-checkbox text-primary-end focus:ring-primary-end"
                  />
                  <span className="text-white text-sm">{t('profile.datingProfile.hookup')}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    ref={restoRef}
                    defaultChecked={profile?.short_term_intentions?.includes('resto')}
                    onChange={markAsDirty}
                    disabled={selectedRelationType !== 'court'}
                    className="form-checkbox text-primary-end focus:ring-primary-end"
                  />
                  <span className="text-white text-sm">{t('profile.datingProfile.resto')}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    ref={drinkRef}
                    defaultChecked={profile?.short_term_intentions?.includes('drink')}
                    onChange={markAsDirty}
                    disabled={selectedRelationType !== 'court'}
                    className="form-checkbox text-primary-end focus:ring-primary-end"
                  />
                  <span className="text-white text-sm">{t('profile.datingProfile.drink')}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    ref={seeWhatRef}
                    defaultChecked={profile?.short_term_intentions?.includes('seeWhat')}
                    onChange={markAsDirty}
                    disabled={selectedRelationType !== 'court'}
                    className="form-checkbox text-primary-end focus:ring-primary-end"
                  />
                  <span className="text-white text-sm">{t('profile.datingProfile.seeWhat')}</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    ref={otherRef}
                    defaultChecked={profile?.short_term_intentions?.includes('other')}
                    onChange={markAsDirty}
                    disabled={selectedRelationType !== 'court'}
                    className="form-checkbox text-primary-end focus:ring-primary-end"
                  />
                  <span className="text-white text-sm">{t('profile.datingProfile.other')}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Intentions Long terme */}
          <div className={`bg-[#0A0A0A] rounded-xl p-3 ${selectedRelationType === 'long' ? '' : 'opacity-50'}`}>
            <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.longTermIntentions')}</div>
            <div className="grid grid-cols-2 gap-1">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  ref={marriageRef}
                  checked={longTermIntentions.marriage}
                  onChange={(e) => {
                    setLongTermIntentions(prev => ({...prev, marriage: e.target.checked}));
                    markAsDirty();
                  }}
                  disabled={selectedRelationType !== 'long'}
                  className="form-checkbox text-primary-end focus:ring-primary-end"
                />
                <span className="text-white text-sm">{t('profile.datingProfile.marriage')}</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  ref={childrenRef}
                  checked={longTermIntentions.children}
                  onChange={(e) => {
                    setLongTermIntentions(prev => ({...prev, children: e.target.checked}));
                    markAsDirty();
                  }}
                  disabled={selectedRelationType !== 'long'}
                  className="form-checkbox text-primary-end focus:ring-primary-end"
                />
                <span className="text-white text-sm">{t('profile.datingProfile.children')}</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  ref={fidelityRef}
                  checked={longTermIntentions.fidelity}
                  onChange={(e) => {
                    setLongTermIntentions(prev => ({...prev, fidelity: e.target.checked}));
                    markAsDirty();
                  }}
                  disabled={selectedRelationType !== 'long'}
                  className="form-checkbox text-primary-end focus:ring-primary-end"
                />
                <span className="text-white text-sm">{t('profile.datingProfile.fidelity')}</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  ref={polygamyRef}
                  checked={longTermIntentions.polygamy}
                  onChange={(e) => {
                    setLongTermIntentions(prev => ({...prev, polygamy: e.target.checked}));
                    markAsDirty();
                  }}
                  disabled={selectedRelationType !== 'long'}
                  className="form-checkbox text-primary-end focus:ring-primary-end"
                />
                <span className="text-white text-sm">{t('profile.datingProfile.polygamy')}</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  ref={freeRef}
                  checked={longTermIntentions.free}
                  onChange={(e) => {
                    setLongTermIntentions(prev => ({...prev, free: e.target.checked}));
                    markAsDirty();
                  }}
                  disabled={selectedRelationType !== 'long'}
                  className="form-checkbox text-primary-end focus:ring-primary-end"
                />
                <span className="text-white text-sm">{t('profile.datingProfile.free')}</span>
              </label>
            </div>
          </div>

          {/* Lien de contact */}
          <div className="bg-[#0A0A0A] rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-1">{t('settings.link.title')}</div>
            <input
              type="text"
              ref={contactLinkRef}
              defaultValue={userProfile.customLinkUrl}
              onBlur={() => {
                // Mettre à jour l'état uniquement lorsque l'utilisateur a fini de saisir
                if (contactLinkRef.current) {
                  let value = contactLinkRef.current.value.trim();
                  
                  // Vérifier si la valeur est un numéro de téléphone (avec différents formats possibles)
                  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
                  
                  if (phoneRegex.test(value) && !value.startsWith('tel:')) {
                    // Supprimer tous les caractères non numériques sauf le + au début
                    const cleanedPhone = value.startsWith('+') 
                      ? '+' + value.substring(1).replace(/\D/g, '') 
                      : value.replace(/\D/g, '');
                    
                    // Convertir en format de lien téléphonique
                    value = `tel:${cleanedPhone}`;
                  } else if (value && !value.startsWith('http://') && !value.startsWith('https://') && !value.startsWith('tel:')) {
                    // Si c'est un lien web sans protocol, ajouter https://
                    value = `https://${value}`;
                  }
                  
                  // Mettre à jour la valeur affichée dans le champ
                  contactLinkRef.current.value = value;
                  
                  setUserProfile(prev => ({ ...prev, customLinkUrl: value }));
                  markAsDirty();
                }
              }}
              placeholder="https://... ou numéro de téléphone"
              className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary-end"
            />
            <div className="text-xs text-gray-500 mt-1">
              {t('settings.link.hint')}
            </div>
          </div>

          {/* Bouton ON/OFF */}
          <div className="bg-[#0A0A0A] rounded-xl p-3">
            <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.visibility')}</div>
            <div className="flex items-center justify-between">
              <span className="text-white">{t('profile.datingProfile.activateVisibility')}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={isProfileVisible} 
                  onChange={(e) => {
                    const newVisibility = e.target.checked;
                    setIsProfileVisible(newVisibility);
                    markAsDirty();
                    
                    // Si l'utilisateur active la visibilité, réinitialiser le compteur de clics
                    if (newVisibility && profile?.contact_clicks && profile.contact_clicks >= 5) {
                      resetContactClicks().then(() => {
                        // Rafraîchir les données du profil pour mettre à jour l'affichage du compteur
                        refresh();
                      }).catch(error => {
                        console.error('Erreur lors de la réinitialisation du compteur de clics:', error);
                      });
                    }
                  }} 
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-start peer-checked:to-primary-end"></div>
              </label>
            </div>
            
            {/* Information sur la désactivation automatique */}
            <div className="mt-2 border-t border-gray-800 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('profile.datingProfile.contactClicks')}</span>
                <span className="text-white font-medium">{profile?.contact_clicks || 0}/5</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('profile.datingProfile.contactClicksInfo')}
              </p>
            </div>
          </div>

          {/* Bouton de sauvegarde */}
          <button
            onClick={handleSaveDatingProfile}
            className={`${gradientButton} w-full rounded-xl py-2 text-white font-medium mt-3 relative`}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 mr-2 relative">
                  <div className="absolute inset-0 border-2 border-white/20 rounded-full"></div>
                  <div className="absolute inset-0 border-2 border-t-white rounded-full animate-spin"></div>
                </div>
                {t('settings.saving')}
              </div>
            ) : (
              saveSuccess ? t('settings.saveSuccess') : t('settings.save')
            )}
          </button>

          {/* Bouton de déconnexion */}
          <button
            onClick={handleLogout}
            className="w-full mt-5 flex items-center justify-center gap-2 text-red-500 bg-red-500/10 rounded-xl p-3 hover:bg-red-500/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('settings.logout')}</span>
          </button>

          {/* Bouton Politique de Confidentialité et CGU */}
          <button
            onClick={() => navigate('/privacy-policy')}
            className="w-full mt-3 flex items-center justify-center gap-2 text-gray-400 bg-gray-800/50 rounded-xl p-3 hover:bg-gray-700/50 transition-colors"
          >
            <span>{t('settings.privacyPolicyButton')}</span>
          </button>
          
          {/* Zone de suppression de compte */}
          <div style={{ display: 'none' }}><DeleteAccount /></div>
        </div>
      </div>
    );
  };

  // Ajouter le composant DeleteAccount
  function DeleteAccount() {
    const { deleteUser } = useAuthContext();
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDeleteAccount = async () => {
      setIsDeleting(true);
      setError(null);
      
      const { error } = await deleteUser();
      
      if (error) {
        setError("Échec de la suppression du compte. Veuillez réessayer.");
        setIsDeleting(false);
      }
    };

    return (
      <div className="mt-12 pt-6 border-t border-gray-800 opacity-80">
        <h3 className="text-sm font-medium text-gray-500">Zone dangereuse</h3>
        
        <button 
          onClick={() => setIsOpen(true)}
          className="mt-2 w-full text-gray-400 border border-gray-700 rounded-xl p-2 text-sm hover:bg-red-900/10 hover:text-red-400 transition-colors"
        >
          Supprimer mon compte
        </button>

        {isOpen && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-900 p-6 rounded-lg max-w-md w-full">
              <h4 className="text-xl font-bold text-red-500">Confirmation de suppression</h4>
              <p className="my-4 text-white">
                Cette action est irréversible. Toutes vos données seront supprimées.
              </p>
              
              {error && <p className="text-red-500 mb-4">{error}</p>}
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 bg-gray-700 rounded"
                  disabled={isDeleting}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="px-4 py-2 bg-red-600 rounded"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Suppression...' : 'Confirmer la suppression'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    if (profile) {
      console.log('Chargement initial du profil:', profile);
      console.log('Pays dans le profil:', profile.country);
      
      // Si le formulaire a été modifié, ne pas permettre aux nouvelles données de profil
      // de remplacer les modifications locales (sauf pour les photos)
      if (formIsDirty) {
        // Récupérer l'état du formulaire capturé
        const capturedState = completeFormStateRef.current;
        if (capturedState) {
          console.log('Le formulaire a des modifications - préservation des modifications locales');
          
          // Si on a capturé l'état, on restaure toutes les valeurs locales
          // tout en mettant à jour uniquement les photos depuis le nouveau profil
          const updatedPhotos = profile.photos || [];
          const updatedAvatar = profile.avatar_url || '';
          
          // Mettre à jour uniquement les photos dans l'état local
          setProfilePhotos(updatedPhotos);
          
          // Mettre à jour l'avatar dans l'état local tout en préservant les autres modifications
          setUserProfile(prev => ({
            ...prev,
            photo: updatedAvatar
          }));
          
          // Restaurer toutes les autres valeurs locales sans toucher aux photos
          restoreFormState();
          
          // Ne pas continuer avec le reste de l'initialisation du profil
          return;
        }
      }
      
      // Poursuivre avec l'initialisation normale du profil si le formulaire n'est pas modifié
      // ou si l'état capturé n'est pas disponible
      
      // Si une invalidation de cache est en cours, restaurer les valeurs précédentes
      if (isCacheInvalidationInProgressRef.current && formValuesBeforeCacheInvalidationRef.current) {
        console.log('Restauration des valeurs après invalidation du cache:', formValuesBeforeCacheInvalidationRef.current);
        setFormValues(formValuesBeforeCacheInvalidationRef.current);
        
        // Mettre à jour les champs non contrôlés via les références
        if (countryInputRef.current) {
          countryInputRef.current.value = formValuesBeforeCacheInvalidationRef.current.country || '';
        }
        if (cityInputRef.current) {
          cityInputRef.current.value = formValuesBeforeCacheInvalidationRef.current.city || '';
        }
        if (ageInputRef.current) {
          ageInputRef.current.value = formValuesBeforeCacheInvalidationRef.current.age || '';
        }
        if (genderSelectRef.current) {
          genderSelectRef.current.value = formValuesBeforeCacheInvalidationRef.current.gender || '';
        }
        if (moneyAmountInputRef.current) {
          moneyAmountInputRef.current.value = formValuesBeforeCacheInvalidationRef.current.moneyAmount || '';
        }
        
        // Restaurer aussi l'état des cases à cocher des intentions
        if (longTermIntentionsStateRef.current) {
          console.log('Restauration des intentions à long terme:', longTermIntentionsStateRef.current);
          
          // Mettre à jour l'état des intentions long terme
          setLongTermIntentions(longTermIntentionsStateRef.current);
        }
        
        isCacheInvalidationInProgressRef.current = false;
        return; // Ne pas réinitialiser les autres valeurs
      }
      
      const relationType = profile.relation_type || 'court';
      
      // Initialiser les valeurs du formulaire (même si les champs sont non contrôlés)
      const country = profile.country || '';
      const city = profile.city || '';
      const age = profile.age?.toString() || '';
      const moneyAmount = relationType === 'long' ? '' : (profile.money_amount?.toString() || '');
      const gender = profile.gender || '';
      const moneyFlow = profile.money_flow || 'give';
      const sexualOrientation = profile.sexual_orientation || 'hetero';
      
      setFormValues({
        country,
        city,
        age,
        moneyAmount,
        gender,
        moneyFlow,
        sexualOrientation
      });
      
      // Mettre à jour directement les champs non contrôlés via les références
      if (countryInputRef.current) {
        countryInputRef.current.value = country;
      }
      if (cityInputRef.current) {
        cityInputRef.current.value = city;
      }
      if (ageInputRef.current) {
        ageInputRef.current.value = age;
      }
      if (genderSelectRef.current) {
        genderSelectRef.current.value = gender;
      }
      if (moneyAmountInputRef.current) {
        moneyAmountInputRef.current.value = moneyAmount;
      }
      
      // Initialiser l'état des intentions long terme à partir du profil
      setLongTermIntentions({
        marriage: profile.long_term_intentions?.includes('marriage') || false,
        children: profile.long_term_intentions?.includes('children') || false,
        fidelity: profile.long_term_intentions?.includes('fidelity') || false,
        polygamy: profile.long_term_intentions?.includes('polygamy') || false,
        free: profile.long_term_intentions?.includes('free') || false
      });
      
      // Définir le type de relation sélectionné
      setSelectedRelationType(relationType);
      
      console.log('Type de relation initial:', relationType);
      console.log('Intentions initiales:', profile.intentions);
      
      setUserProfile({
        name: profile.full_name || profile.email?.split('@')[0] || 'Utilisateur',
        photo: profile.avatar_url || '',
        customLink: profile.custom_link || '',
        customLinkUrl: profile.custom_link_url || '',
        email: profile.email || '',
        description: profile.description || ''
      });
      
      // États indépendants
      setIsProfileVisible(profile.is_visible || false);
      setProfilePhotos(profile.photos || []);
    }
  }, [profile, formIsDirty, restoreFormState]);

  useEffect(() => {
    // Nous ne voulons plus de vérification avant de quitter la page
    
    return () => {
      // Nettoyage des écouteurs d'événements
    };
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Styles personnalisés pour les cases à cocher et boutons radio */}
      <style>
        {`
          .form-checkbox:checked, .form-radio:checked {
            background-image: linear-gradient(to right, #B8A44E, #ec4899);
            border-color: transparent;
          }
          .form-checkbox:focus, .form-radio:focus {
            box-shadow: 0 0 0 2px rgba(184, 164, 78, 0.3);
            border-color: transparent;
          }
        `}
      </style>
      
      {/* Boîte de dialogue pour les modifications non sauvegardées - supprimée */}

      {showImageCropper && tempPhotoUrl && (
        <ImageCropper
          imageUrl={tempPhotoUrl}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowImageCropper(false);
            setTempPhotoUrl(null);
          }}
          aspectRatio={1}
        />
      )}
      
      <div className="relative">
        {/* Header avec dégradé */}
        <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-r from-primary-start to-primary-end opacity-10 rounded-b-[40px]" />
        
        {/* Contenu */}
        <div className="relative container mx-auto px-4 pt-6 pb-20">
          {/* Crawl informatif avec bouton retour superposé */}
          <div className="relative w-full mb-6">
            {/* Crawl informatif */}
            <div className="w-full overflow-hidden bg-gradient-to-r from-black to-[#0A0A0A] rounded-xl py-3 border-l border-primary-start/30 shadow-md relative crawl-container crawl-glow light-sweep">
              {/* Effet de bordure brillante - opacité réduite */}
              <div className="absolute inset-0 border border-primary-start/10 rounded-xl"></div>
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-start to-primary-end opacity-30"></div>
              <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-start to-primary-end opacity-40"></div>
              
              {/* Effet lumineux accentué sur la bordure gauche - subtil */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-start via-primary-end to-primary-start opacity-20 blur-sm"></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-0.5 bg-primary-end opacity-40 blur-[1px] animate-pulse"></div>
              
              <div className="relative pl-12">
                <div className="crawl-text text-sm">
                  <span>
                    <span className="highlight-word">{t('settings.crawlText')}</span>
                    <span className="mx-28">•</span>
                    <span className="highlight-word">{t('settings.crawlText')}</span>
                    <span className="mx-28">•</span>
                    <span className="highlight-word">{t('settings.crawlText')}</span>
                  </span>
                </div>
              </div>
            </div>
            
            {/* Bouton de retour superposé */}
            {!isNewUser && (
              <button 
                onClick={() => navigateWithCheck('/profile')} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white hover:opacity-80 transition-opacity z-10"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            )}
          </div>

          <div className="max-w-lg mx-auto">
            <ProfilePhotoSection />

            {/* Liste des paramètres */}
            <div className="space-y-3">
              {/* Nom */}
              <div className="bg-[#0A0A0A] rounded-xl p-3">
                <div className="text-sm text-gray-400 mb-1">{t('settings.name.title')}</div>
                <input
                  type="text"
                  value={userProfile.name}
                  onChange={(e) => {
                    setUserProfile(prev => ({ ...prev, name: e.target.value }));
                    markAsDirty();
                  }}
                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                />
              </div>

              {/* Description - nouvelle section */}
              <div className="bg-[#0A0A0A] rounded-xl p-3">
                <div className="text-sm text-gray-400 mb-1">{t('settings.description.title')}</div>
                <div className="relative">
                  <textarea
                    value={userProfile.description}
                    onChange={(e) => {
                      // Limiter à 500 caractères
                      if (e.target.value.length <= 500) {
                        setUserProfile(prev => ({ ...prev, description: e.target.value }));
                        markAsDirty();
                      }
                    }}
                    placeholder={t('settings.description.placeholder')}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary-end resize-none min-h-[100px]"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {t('settings.description.charCount', { current: userProfile.description.length })}
                  </div>
                </div>
              </div>

              {/* Pays et ville */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0A0A0A] rounded-xl p-3">
                  <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.country')}</div>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 z-10" />
                    <input
                      type="text"
                      ref={countryInputRef}
                      defaultValue={formValues.country}
                      onChange={markAsDirty}
                      placeholder={t('profile.datingProfile.countryPlaceholder')}
                      className="w-full bg-black border border-gray-800 rounded-lg pl-8 pr-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                    />
                  </div>
                </div>
                <div className="bg-[#0A0A0A] rounded-xl p-3">
                  <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.city')}</div>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 z-10" />
                    <input
                      type="text"
                      ref={cityInputRef}
                      defaultValue={formValues.city}
                      onChange={markAsDirty}
                      placeholder={t('profile.datingProfile.cityPlaceholder')}
                      className="w-full bg-black border border-gray-800 rounded-lg pl-8 pr-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                    />
                  </div>
                </div>
              </div>

              {/* Sexe et âge */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#0A0A0A] rounded-xl p-3">
                  <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.gender')}</div>
                  <select
                    ref={genderSelectRef}
                    defaultValue={formValues.gender}
                    onChange={markAsDirty}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                  >
                    <option value="homme">{t('profile.datingProfile.genderOption.male')}</option>
                    <option value="femme">{t('profile.datingProfile.genderOption.female')}</option>
                  </select>
                </div>
                <div className="bg-[#0A0A0A] rounded-xl p-3">
                  <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.age')}</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    ref={ageInputRef}
                    defaultValue={formValues.age}
                    onChange={markAsDirty}
                    placeholder={t('profile.datingProfile.agePlaceholder')}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-1.5 text-white focus:outline-none focus:border-primary-end"
                  />
                </div>
              </div>

              {/* Orientation sexuelle */}
              <div className="bg-[#0A0A0A] rounded-xl p-3">
                <div className="text-sm text-gray-400 mb-1">{t('profile.datingProfile.orientation.title')}</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 p-2 border border-gray-700 rounded-lg cursor-pointer hover:border-primary-end/50 transition-colors">
                    <input
                      type="radio"
                      name="sexualOrientation"
                      value="hetero"
                      checked={formValues.sexualOrientation === 'hetero'}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, sexualOrientation: e.target.value }));
                        markAsDirty();
                      }}
                      className="form-radio text-primary-end focus:ring-primary-end"
                    />
                    <span className="text-white text-sm">{t('profile.datingProfile.orientation.hetero')}</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 border border-gray-700 rounded-lg cursor-pointer hover:border-primary-end/50 transition-colors">
                    <input
                      type="radio"
                      name="sexualOrientation"
                      value="gay"
                      checked={formValues.sexualOrientation === 'gay'}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, sexualOrientation: e.target.value }));
                        markAsDirty();
                      }}
                      className="form-radio text-primary-end focus:ring-primary-end"
                    />
                    <span className="text-white text-sm">{t('profile.datingProfile.orientation.gay')}</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 border border-gray-700 rounded-lg cursor-pointer hover:border-primary-end/50 transition-colors">
                    <input
                      type="radio"
                      name="sexualOrientation"
                      value="lesbian"
                      checked={formValues.sexualOrientation === 'lesbian'}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, sexualOrientation: e.target.value }));
                        markAsDirty();
                      }}
                      className="form-radio text-primary-end focus:ring-primary-end"
                    />
                    <span className="text-white text-sm">{t('profile.datingProfile.orientation.lesbian')}</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 border border-gray-700 rounded-lg cursor-pointer hover:border-primary-end/50 transition-colors">
                    <input
                      type="radio"
                      name="sexualOrientation"
                      value="bi"
                      checked={formValues.sexualOrientation === 'bi'}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, sexualOrientation: e.target.value }));
                        markAsDirty();
                      }}
                      className="form-radio text-primary-end focus:ring-primary-end"
                    />
                    <span className="text-white text-sm">{t('profile.datingProfile.orientation.bi')}</span>
                  </label>
                </div>
              </div>

              <DatingProfileSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings; 